
import { useState } from "react";
import { CardContainer } from "./event-card/CardContainer";
import { CardContent } from "./event-card/CardContent";
import { EventImage } from "./event-card/EventImage";
import { useEventCard } from "./event-card/useEventCard";
import { EventCardProps } from "./event-card/types";
import { RecommendationModal } from "./event-card/RecommendationModal";
import { useEventGuests } from "@/hooks/use-event-guests";
import { GuestSection } from "./event-card/GuestSection";
import { getEventLocationAndDate } from "./event-card/EventLocationDate";
import { EventCardHeader } from "./event-card/EventCardHeader";
import { EventCardFooter } from "./event-card/EventCardFooter";

export const EventCard = ({
  eventId,
  title,
  date,
  image,
  status,
  guests,
  isHost,
  archived = false,
  onArchiveToggle,
  locationType,
  flexibleLocationDescription,
  preferredDates,
  preferredVenues,
  recommendations = {
    venues: {
      total: 0,
      selected: 0
    },
    dates: {
      total: 0,
      selected: 0
    }
  },
  event_venue_recommendations = [],
  event_date_recommendations = [],
  description,
  event_images,
  event_deadline,
}: EventCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    handleCardClick,
    selectedVenue,
    selectedDate
  } = useEventCard(eventId, status, event_venue_recommendations, event_date_recommendations, recommendations);

  const handleReviewClick = () => {
    setIsModalOpen(true);
  };

  // Get guest data using the custom hook
  const { 
    hostUser,
    acceptedGuests,
    invitedGuests,
    isLoadingInvites
  } = useEventGuests(eventId);

  // Get the location and date display values
  const { displayLocation, displayDate } = getEventLocationAndDate({
    status,
    flexibleLocationDescription,
    event_venue_recommendations,
    event_date_recommendations,
    date
  });

  // Create a fallback for guests if we haven't loaded the real data yet
  const fallbackGuests = Array(guests).fill(null).map(() => ({ 
    full_name: "", 
    rsvp_status: 'invited' as const
  }));

  // Check if all guests have RSVP'd (either accepted or declined)
  const allGuestsResponded = invitedGuests.length > 0 && 
    invitedGuests.every(guest => 
      guest.rsvp_status === 'accepted' || guest.rsvp_status === 'declined'
    );
    
  // Determine if this card should be highlighted (confirmed or booked status)
  const isConfirmed = status === 'confirmed' || status === 'booked';

  return (
    <div className="mb-4 py-0 my-[20px]">
      <CardContainer 
        onClick={handleCardClick} 
        status={status}
        highlight={isConfirmed}
      >
        {/* 1. Title section at the top */}
        <div className="p-4 pb-0">
          <EventCardHeader 
            title={title} 
            isHost={isHost}
            deadline={status !== 'confirmed' && status !== 'booked' ? event_deadline : null}
            guestRsvpComplete={allGuestsResponded}
            hasRecommendations={event_date_recommendations.length > 0}
          />
        </div>

        {/* 2. Guest list + image section */}
        <div className="grid grid-cols-2 gap-4 relative mb-2">
          <GuestSection 
            hostUser={hostUser}
            acceptedGuests={acceptedGuests}
            invitedGuests={isLoadingInvites ? fallbackGuests : invitedGuests}
            isLoading={isLoadingInvites}
          />
          
          <div>
            <EventImage image={image} title={title} eventImages={event_images} />
          </div>
        </div>

        {/* Separator line */}
        <div className="border-t border-gray-100 mx-4"></div>

        {/* 3. & 4. Description, status, and buttons */}
        <div className="relative z-20">
          <CardContent
            date={displayDate}
            location={displayLocation}
            status={status}
            locationType={locationType}
            flexibleLocationDescription={flexibleLocationDescription}
            preferredDates={preferredDates}
            preferredVenues={preferredVenues}
            guests={guests}
            archived={archived}
            onArchiveToggle={onArchiveToggle}
            recommendations={recommendations}
            description={description}
            eventId={eventId}
            showDescription={false}
          />
        </div>
        
        {/* Footer with guest avatars and review recommendations button */}
        <div className="p-4 pt-0">
          <EventCardFooter
            status={status}
            guests={invitedGuests}
            archived={archived}
            onArchiveToggle={onArchiveToggle}
            recommendations={recommendations}
            isHost={isHost}
            onReviewClick={handleReviewClick}
            event_date_recommendations={event_date_recommendations}
          />
        </div>
      </CardContainer>

      <RecommendationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        eventId={eventId}
        eventName={title}
        dateRecommendations={event_date_recommendations}
        venueRecommendations={event_venue_recommendations}
      />
    </div>
  );
};
